import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import ProfileForm from "@/components/ProfileForm";
import { NumberCompare } from "@/components/NumberCompare";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Price } from "@/components/common";
import { Switch } from "@/components/ui/switch";
import { compressImageToDataUrl } from "@/lib/image";
import { Copy, Gift, Wallet, ClipboardList, UserRound, Users, Quote, PenLine, Upload, Eye, Mail, Send, Scale } from "lucide-react";

const statusColor = { pending: "bg-amber-100 text-amber-800", approved: "bg-green-100 text-green-800", rejected: "bg-red-100 text-red-700" };

function EmailSubCard() {
  const { t, lang } = useI18n();
  const { user, refreshUser, updateProfile } = useAuth();
  const [configured, setConfigured] = useState(null);
  const [busy, setBusy] = useState(false);
  const optIn = user?.email_fortune_opt_in !== false;

  useEffect(() => {
    api.get("/email/status").then((r) => setConfigured(r.data.configured)).catch(() => setConfigured(false));
  }, []);

  const toggle = async (v) => {
    try { await updateProfile({ email_fortune_opt_in: v }); await refreshUser(); toast.success(t("profileForm.saved")); }
    catch { toast.error("Error"); }
  };

  const sendNow = async (kind) => {
    setBusy(true);
    try {
      const r = await api.post("/email/fortune/send-now", { kind, lang });
      if (r.data.configured === false) toast.info(t("emailx.notConfigured"));
      else if (r.data.ok) toast.success(t("emailx.sent"));
      else toast.error(t("emailx.sendFail"));
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };

  return (
    <Card className="border-border max-w-xl mt-4" data-testid="email-sub-card"><CardContent className="p-6 space-y-4">
      <div className="flex items-center gap-2"><Mail className="h-4 w-4 text-[hsl(var(--brand-bronze))]" /><span className="font-semibold">{t("emailx.title")}</span></div>
      {configured === false && <div className="rounded-lg bg-[hsl(var(--secondary))] p-3 text-xs text-muted-foreground" data-testid="email-not-configured">{t("emailx.notConfiguredHint")}</div>}
      <div className="flex items-center justify-between gap-3">
        <div><div className="text-sm font-medium">{t("emailx.optInLabel")}</div><div className="text-xs text-muted-foreground">{t("emailx.optInDesc")}</div></div>
        <Switch checked={optIn} onCheckedChange={toggle} data-testid="email-optin-switch" />
      </div>
      <div className="flex gap-2 flex-wrap">
        <Button variant="outline" size="sm" className="border-[hsl(var(--brand-gold))]" disabled={busy || !optIn} onClick={() => sendNow("daily")} data-testid="email-send-daily"><Send className="h-3.5 w-3.5 mr-1" />{t("emailx.sendDaily")}</Button>
        <Button variant="outline" size="sm" className="border-[hsl(var(--brand-gold))]" disabled={busy || !optIn} onClick={() => sendNow("weekly")} data-testid="email-send-weekly"><Send className="h-3.5 w-3.5 mr-1" />{t("emailx.sendWeekly")}</Button>
      </div>
    </CardContent></Card>
  );
}


function SavedNumerology({ lang }) {
  const [items, setItems] = useState(null);
  const load = useCallback(() => {
    api.get("/numerology/saved").then((r) => setItems(r.data.items)).catch(() => setItems([]));
  }, []);
  useEffect(() => { load(); }, [load]);
  const del = async (id) => {
    try { await api.delete(`/numerology/saved/${id}`); toast.success(lang === "zh" ? "已删除" : "Deleted"); load(); }
    catch (e) { toast.error("Error"); }
  };
  if (items === null) return <div className="py-8 text-center text-muted-foreground">…</div>;
  if (items.length === 0) return <div className="py-10 text-center text-muted-foreground text-sm" data-testid="saved-numerology-empty">{lang === "zh" ? "暂无保存的记录。在「易经数字」页分析后点「保存记录」即可。" : "No saved records yet. Use 'Save' on the Yijing Numbers page."}</div>;
  const kindLabel = (k) => k === "phone" ? (lang === "zh" ? "数字能量" : "Phone Energy") : k === "daily" ? (lang === "zh" ? "每日一卦" : "Daily") : (lang === "zh" ? "易经起卦" : "Hexagram");
  return (
    <div className="space-y-3" data-testid="saved-numerology">
      {items.map((it) => (
        <Card key={it.id} className="border-border"><CardContent className="p-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <Badge variant="outline">{kindLabel(it.kind)}</Badge>
                <span className="font-semibold truncate">{it.label || "—"}</span>
              </div>
              {it.note && <div className="text-xs text-[hsl(var(--brand-bronze))] mt-0.5">{lang === "zh" ? "备注：" : "Note: "}{it.note}</div>}
              <div className="text-xs text-muted-foreground mt-0.5">{(it.created_at || "").slice(0, 10)}</div>
              {it.kind === "phone" && it.result && <div className="text-sm mt-1">{lang === "zh" ? "评分" : "Score"} <span className="font-mono-num font-bold text-[hsl(var(--brand-vermillion))]">{it.result.score}</span> · {it.result.dominant}</div>}
              {(it.kind === "hexagram" || it.kind === "daily") && it.result && <div className="text-sm mt-1">{it.result.ben_gua?.name} → {it.result.bian_gua?.name}</div>}
              {it.ai_reading?.summary && <p className="text-xs text-muted-foreground mt-1">{it.ai_reading.summary}</p>}
            </div>
            <Button size="sm" variant="ghost" onClick={() => del(it.id)} data-testid={`saved-del-${it.id}`} className="text-[hsl(var(--brand-vermillion))] shrink-0">{lang === "zh" ? "删除" : "Delete"}</Button>
          </div>
        </CardContent></Card>
      ))}
    </div>
  );
}

export default function Profile() {
  const { t, lang, pick } = useI18n();
  const { user, refreshUser, updateProfile } = useAuth();
  const [params] = useSearchParams();
  const [tab, setTab] = useState(params.get("tab") || "projects");
  const showWelcome = params.get("welcome") === "1";
  const [orders, setOrders] = useState([]);
  const [ref, setRef] = useState(null);
  const [pts, setPts] = useState(null);
  const [testis, setTestis] = useState([]);
  const [tform, setTform] = useState({ name: user?.name || "", message: "" });
  const [tbusy, setTbusy] = useState(false);
  const [uploadOrder, setUploadOrder] = useState(null);
  const [rData, setRData] = useState("");
  const [rRef, setRRef] = useState("");
  const [rBusy, setRBusy] = useState(false);
  const [viewReceipt, setViewReceipt] = useState(null);

  const load = useCallback(() => {
    api.get("/orders/mine").then((r) => setOrders(r.data)).catch(() => {});
    api.get("/referral/summary").then((r) => setRef(r.data)).catch(() => {});
    api.get("/referral/points").then((r) => setPts(r.data)).catch(() => {});
    api.get("/testimonials/mine").then((r) => setTestis(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const openUpload = (o) => { setUploadOrder(o); setRData(o.receipt_data && !o.receipt_by_admin ? o.receipt_data : ""); setRRef(o.bank_reference || ""); };
  const onReceiptFile = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 8 * 1024 * 1024) { toast.error(lang === "zh" ? "图片需小于 8MB" : "Image must be < 8MB"); return; }
    try { setRData(await compressImageToDataUrl(f)); }
    catch { toast.error(lang === "zh" ? "读取失败" : "Read failed"); }
    e.target.value = "";
  };
  const saveReceipt = async () => {
    if (!rData) { toast.error(lang === "zh" ? "请先选择付款证明图片" : "Select a receipt image first"); return; }
    setRBusy(true);
    try {
      await api.post(`/orders/${uploadOrder.id}/receipt`, { receipt_data: rData, bank_reference: rRef });
      toast.success(t("profile.receiptUploaded"));
      setUploadOrder(null); setRData(""); setRRef(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setRBusy(false); }
  };

  const submitTesti = async () => {
    if (!tform.message.trim()) { toast.error(lang === "zh" ? "请填写见证内容" : "Please write your testimonial"); return; }
    setTbusy(true);
    try {
      await api.post("/testimonials", tform);
      toast.success(t("testimonials.thanks"));
      setTform({ name: user?.name || "", message: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setTbusy(false); }
  };

  const refLink = `${window.location.origin}/member?ref=${user?.referral_code || ""}`;
  const copy = async (v) => { await navigator.clipboard.writeText(v); toast.success(t("common.copied")); };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <h1 className="font-display text-2xl sm:text-3xl font-bold mb-6">{t("profile.title")}</h1>

      <div className="grid md:grid-cols-3 gap-4 mb-6">
        <Card className="border-border"><CardContent className="p-4 flex items-center gap-3">
          <div className="h-11 w-11 rounded-lg bg-[hsl(var(--brand-gold-soft))] flex items-center justify-center"><Wallet className="h-5 w-5 text-[hsl(var(--brand-bronze))]" /></div>
          <div><div className="text-xs text-muted-foreground">{t("profile.myPoints")}</div><div className="font-mono-num text-xl font-bold">{Number(user?.points || 0).toFixed(2)}</div></div>
        </CardContent></Card>
        <Card className="border-border"><CardContent className="p-4 flex items-center gap-3">
          <div className="h-11 w-11 rounded-lg bg-[hsl(var(--brand-gold-soft))] flex items-center justify-center"><ClipboardList className="h-5 w-5 text-[hsl(var(--brand-bronze))]" /></div>
          <div><div className="text-xs text-muted-foreground">{t("profile.myProjects")}</div><div className="font-mono-num text-xl font-bold">{orders.length}</div></div>
        </CardContent></Card>
        <Card className="border-border"><CardContent className="p-4 flex items-center gap-3">
          <div className="h-11 w-11 rounded-lg bg-[hsl(var(--brand-gold-soft))] flex items-center justify-center"><Users className="h-5 w-5 text-[hsl(var(--brand-bronze))]" /></div>
          <div><div className="text-xs text-muted-foreground">{t("profile.whoJoined")}</div><div className="font-mono-num text-xl font-bold">{ref?.count ?? 0}</div></div>
        </CardContent></Card>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex flex-wrap h-auto">
          <TabsTrigger value="projects" data-testid="profile-tab-projects">{t("profile.myProjects")}</TabsTrigger>
          <TabsTrigger value="referral" data-testid="profile-tab-referral">{t("profile.referral")}</TabsTrigger>
          <TabsTrigger value="points" data-testid="profile-tab-points">{t("profile.ledger")}</TabsTrigger>
          <TabsTrigger value="testimonials" data-testid="profile-tab-testimonials">{t("testimonials.title")}</TabsTrigger>
          <TabsTrigger value="info" data-testid="profile-tab-info">{t("profile.info")}</TabsTrigger>
          <TabsTrigger value="numerology" data-testid="profile-tab-numerology">{lang === "zh" ? "易经数字" : "Yijing Numbers"}</TabsTrigger>
        </TabsList>

        <TabsContent value="projects" className="mt-4">
          {orders.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">{t("profile.noOrders")}</div>
          ) : (
            <div className="space-y-3">
              {orders.map((o) => {
                const needsProof = !o.is_free && Number(o.cash_amount) > 0 && o.status !== "approved";
                const hasReceipt = !!o.receipt_data;
                return (
                <Card key={o.id} className="border-border" data-testid={`profile-order-${o.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div>
                        <div className="font-semibold">{lang === "zh" ? o.project_title : (o.project_title_en || o.project_title)}</div>
                        <div className="text-xs text-muted-foreground mt-0.5">{(o.created_at || "").slice(0, 10)} · {t("common.quantity")} {o.quantity}</div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Price value={o.amount} />
                        <Badge className={statusColor[o.status]}>{t(`orderStatus.${o.status}`)}</Badge>
                      </div>
                    </div>
                    {(needsProof || hasReceipt) && (
                      <div className="mt-3 pt-3 border-t border-border flex items-center gap-2 flex-wrap">
                        {hasReceipt ? (
                          <>
                            <Button size="sm" variant="outline" className="h-8" onClick={() => setViewReceipt(o.receipt_data)} data-testid={`profile-view-receipt-${o.id}`}><Eye className="h-3.5 w-3.5 mr-1" />{t("profile.viewReceipt")}</Button>
                            {o.status !== "approved" && (
                              <Button size="sm" variant="ghost" className="h-8 text-[hsl(var(--brand-bronze))]" onClick={() => openUpload(o)} data-testid={`profile-reupload-receipt-${o.id}`}><Upload className="h-3.5 w-3.5 mr-1" />{t("profile.reupload")}</Button>
                            )}
                          </>
                        ) : (
                          <>
                            <span className="text-xs text-[hsl(var(--brand-vermillion))]">{t("profile.awaitingReceipt")}</span>
                            <Button size="sm" variant="outline" className="h-8 border-[hsl(var(--brand-gold))] ml-auto" onClick={() => openUpload(o)} data-testid={`profile-upload-receipt-${o.id}`}><Upload className="h-3.5 w-3.5 mr-1" />{t("profile.uploadReceipt")}</Button>
                          </>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="referral" className="mt-4">
          <Card className="border-border">
            <CardContent className="p-6">
              <div className="flex items-start gap-3 rounded-lg bg-[hsl(var(--brand-jade-mist))] p-4 mb-5">
                <Gift className="h-5 w-5 text-[hsl(var(--brand-bronze))] mt-0.5" />
                <p className="text-sm">{t("profile.referralDesc")}</p>
              </div>
              <div className="grid sm:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <div className="text-xs text-muted-foreground mb-1">{t("profile.referralCode")}</div>
                    <div className="flex gap-2"><Input readOnly value={user?.referral_code || ""} className="font-mono-num" data-testid="referral-code" /><Button variant="outline" size="icon" onClick={() => copy(user?.referral_code)}><Copy className="h-4 w-4" /></Button></div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground mb-1">{t("profile.referralLink")}</div>
                    <div className="flex gap-2"><Input readOnly value={refLink} className="text-xs" data-testid="referral-link" /><Button variant="outline" size="icon" onClick={() => copy(refLink)} data-testid="copy-referral-link"><Copy className="h-4 w-4" /></Button></div>
                  </div>
                  <div className="text-sm"><span className="text-muted-foreground">{t("profile.totalReward")}: </span><span className="font-mono-num font-bold text-[hsl(var(--brand-vermillion))]">{(ref?.total_reward_points ?? 0).toFixed(2)} {t("common.pointsUnit")}</span></div>
                </div>
                <div className="flex justify-center items-start">
                  <div className="rounded-xl border border-border p-3 bg-white"><QRCodeSVG value={refLink} size={140} fgColor="#1a1712" /></div>
                </div>
              </div>
              <div className="mt-6">
                <div className="font-semibold mb-2">{t("profile.whoJoined")}</div>
                {(ref?.referrals || []).length === 0 ? (
                  <div className="text-sm text-muted-foreground">{t("profile.noReferral")}</div>
                ) : (
                  <div className="space-y-2">
                    {ref.referrals.map((r) => (
                      <div key={r.id} className="flex items-center justify-between rounded-lg border border-border p-3">
                        <div className="flex items-center gap-2"><UserRound className="h-4 w-4 text-[hsl(var(--brand-bronze))]" /><span className="font-semibold">{r.referred_name}</span></div>
                        <span className="text-xs text-muted-foreground">{(r.created_at || "").slice(0, 10)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="points" className="mt-4">
          <Card className="border-border"><CardContent className="p-4">
            {(pts?.ledger || []).length === 0 ? (
              <div className="text-center py-10 text-muted-foreground">{lang === "zh" ? "暂无点数记录" : "No records"}</div>
            ) : (
              <div className="divide-y divide-border">
                {pts.ledger.map((l) => (
                  <div key={l.id} className="flex items-center justify-between py-3">
                    <div><div className="text-sm">{l.description}</div><div className="text-xs text-muted-foreground">{(l.created_at || "").slice(0, 16).replace("T", " ")}</div></div>
                    <div className={`font-mono-num font-bold ${l.amount >= 0 ? "text-green-700" : "text-red-600"}`}>{l.amount >= 0 ? "+" : ""}{Number(l.amount).toFixed(2)}</div>
                  </div>
                ))}
              </div>
            )}
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="info" className="mt-4">
          {showWelcome && (
            <div className="max-w-xl mb-4 rounded-lg border border-[hsl(var(--brand-gold))] bg-[hsl(var(--brand-gold-soft)/0.3)] p-4" data-testid="profile-complete-banner">
              <div className="flex items-start gap-3">
                <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-[hsl(var(--brand-gold)/0.2)] text-[hsl(var(--brand-bronze))]"><UserRound className="h-4.5 w-4.5" /></span>
                <div>
                  <div className="font-display font-semibold text-[hsl(var(--brand-ink))]">{lang === "zh" ? "欢迎加入你好功德！请完善个人资料" : "Welcome to CiAO Merit! Complete your profile"}</div>
                  <p className="text-sm text-muted-foreground mt-1">{lang === "zh" ? "填写出生年月日与时辰后，即可生成八字命盘与每日 / 每周运势。" : "Add your birth date & time to unlock your BaZi chart and daily / weekly fortune."}</p>
                </div>
              </div>
            </div>
          )}
          <Card className="border-border max-w-xl"><CardContent className="p-6">
            <ProfileForm />
          </CardContent></Card>
          <EmailSubCard />
        </TabsContent>

        <TabsContent value="numerology" className="mt-4">
          <div className="space-y-6">
            <div>
              <div className="flex items-center justify-between gap-2 mb-3">
                <h3 className="font-display font-semibold text-[hsl(var(--brand-ink))]">{lang === "zh" ? "保存的记录" : "Saved records"}</h3>
                <Link to="/numerology?tab=compare"><Button size="sm" variant="outline" className="border-[hsl(var(--brand-gold))] text-[hsl(var(--brand-bronze))]" data-testid="profile-open-numerology"><Scale className="h-4 w-4 mr-1" />{lang === "zh" ? "去易经数字" : "Open Yijing"}</Button></Link>
              </div>
              <SavedNumerology lang={lang} />
            </div>
            <div className="pt-2 border-t border-border">
              <h3 className="font-display font-semibold text-[hsl(var(--brand-ink))] mb-1 flex items-center gap-1.5"><Scale className="h-4 w-4 text-[hsl(var(--brand-bronze))]" />{lang === "zh" ? "号码收藏对比" : "Compare numbers"}</h3>
              <p className="text-xs text-muted-foreground mb-3">{lang === "zh" ? "从收藏的号码中挑出综合评分最旺的一个。" : "Pick the highest-scoring number from your collection."}</p>
              <NumberCompare />
            </div>
          </div>
        </TabsContent>

        <TabsContent value="testimonials" className="mt-4">
          <div className="grid md:grid-cols-2 gap-4">
            <Card className="border-border"><CardContent className="p-6">
              <div className="flex items-center gap-2 mb-3"><PenLine className="h-4 w-4 text-[hsl(var(--brand-bronze))]" /><span className="font-semibold">{t("testimonials.submitTitle")}</span></div>
              <div className="space-y-3">
                <div><label className="text-xs text-muted-foreground">{t("testimonials.yourName")}</label><Input value={tform.name} onChange={(e) => setTform({ ...tform, name: e.target.value })} data-testid="profile-testi-name" /></div>
                <div><label className="text-xs text-muted-foreground">{t("testimonials.yourMessage")}</label><Textarea rows={4} value={tform.message} onChange={(e) => setTform({ ...tform, message: e.target.value })} data-testid="profile-testi-message" /></div>
                <p className="text-xs text-muted-foreground">{t("testimonials.pendingNote")}</p>
                <Button className="w-full bg-[hsl(var(--primary))] text-primary-foreground" disabled={tbusy} onClick={submitTesti} data-testid="profile-testi-submit">{t("testimonials.submitBtn")}</Button>
              </div>
            </CardContent></Card>
            <Card className="border-border"><CardContent className="p-6">
              <div className="flex items-center gap-2 mb-3"><Quote className="h-4 w-4 text-[hsl(var(--brand-bronze))]" /><span className="font-semibold">{t("testimonials.myTitle")}</span></div>
              {testis.length === 0 ? (
                <div className="text-sm text-muted-foreground py-6 text-center">{t("testimonials.empty")}</div>
              ) : (
                <div className="space-y-3">
                  {testis.map((it) => (
                    <div key={it.id} className="rounded-lg border border-border p-3">
                      <p className="text-sm">{it.message}</p>
                      <div className="mt-2 flex items-center justify-between">
                        <span className="text-xs text-muted-foreground">{(it.created_at || "").slice(0, 10)}</span>
                        <Badge className={statusColor[it.status]}>{t(`testimonials.status${it.status.charAt(0).toUpperCase() + it.status.slice(1)}`)}</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent></Card>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={!!uploadOrder} onOpenChange={(o) => { if (!o) { setUploadOrder(null); setRData(""); setRRef(""); } }}>
        <DialogContent className="max-w-lg bg-card max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{t("profile.uploadReceipt")}</DialogTitle></DialogHeader>
          {uploadOrder && (
            <div className="space-y-3">
              <div className="rounded-lg bg-[hsl(var(--secondary))] p-3 text-sm">
                <div className="font-semibold">{lang === "zh" ? uploadOrder.project_title : (uploadOrder.project_title_en || uploadOrder.project_title)}</div>
                <div className="text-xs text-muted-foreground">RM{Number(uploadOrder.cash_amount).toFixed(2)}</div>
              </div>
              <p className="text-xs text-muted-foreground">{t("profile.uploadReceiptDesc")}</p>
              <div><Label>{t("profile.receiptImage")}</Label><input type="file" accept="image/*" onChange={onReceiptFile} className="block w-full text-sm mt-1" data-testid="profile-receipt-file" /></div>
              {rData && <img src={rData} alt="" className="w-full rounded-lg border border-border max-h-64 object-contain bg-black/5" data-testid="profile-receipt-preview" />}
              <div><Label>{t("profile.bankRef")}</Label><Input value={rRef} onChange={(e) => setRRef(e.target.value)} placeholder={lang === "zh" ? "如：转账参考号 / 银行" : "e.g. transfer ref / bank"} data-testid="profile-receipt-ref" /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setUploadOrder(null); setRData(""); setRRef(""); }}>{t("common.cancel")}</Button>
            <Button className="bg-[hsl(var(--primary))] text-primary-foreground" disabled={rBusy} onClick={saveReceipt} data-testid="profile-receipt-save">{t("bankin.submitReceipt")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!viewReceipt} onOpenChange={() => setViewReceipt(null)}>
        <DialogContent className="max-w-lg bg-card"><DialogHeader><DialogTitle>{t("profile.viewReceipt")}</DialogTitle></DialogHeader>
          {viewReceipt && <img src={viewReceipt} alt="receipt" className="w-full rounded-lg border border-border" />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
